/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 */

import { getAPIFriendlyFilters } from '@zcode-ui/filters'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { type RawLocalUser, User } from '@zcode/bizos'
import { isCommonAuthError, isCookieError } from '@zcode/error-filters'
import { EVENT_NAMES, PROP_NAMES, identify, track } from '@zcode/mixpanel'
import { localStorageSingleton, saveUserToLocalStorage, trackGAEvent } from '@zcode/nr-utils'
import { search } from '@zcode/nr/es/shared/api'
import { SavedSearch } from '@zcode/nr/es/shared/bizos/searches'
import { selectUser } from '@zcode/nr/es/shared/modules/user/selectors'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import { ALERT_CREATED } from '@zcode/nr/es/shared/thirdParty/optimizely/constants'
import Marketing from '@zcode/nr/es/shared/utils/marketing'
import { getVisitedExperiments } from '@zcode/optimizely-client'
import type {
  ExtraThunkArg,
  Filters,
  FloorplanListing,
  Listable,
  NormalFloorplanListingData,
  Property,
} from '@zcode/state-type'
import { addErrorButterBar } from '../../../../actions/butter.action'
import { setLocalUser } from '../../../../actions/index'
import { ALERT_CREATION_TOAST_TYPE } from '../../../../components/Notification/constants/index'
import { REFERRERS } from '../../../../constants/sign_in'
import { logger } from '../../../../logger'
import { pushSignInUrl, pushSignUpUrl } from '../../../account/thunks'
import { setSavedSearchEmail, setToastType } from '../../../currentSearchView/actions/index'
import { getFormattedOrigin } from '../../../mixpanel/helpers/getFormattedOrigin'
import { getPageNameFromRouteProps } from '../../../mixpanel/helpers/getPageNameFromRouteProps'
import {
  doneCreatingSavedSearch,
  errorCreatingSavedSearch,
  startCreatingSavedSearch,
} from '../../../modals/actions/saveSearchModal'
import type { Dispatch, GetState } from '../../../types'
import { updateSavedSearches } from '../../actions'
import {
  API_RESPONSE,
  DEFAULT_ALERT_FREQUENCY,
  type HOOD_SEARCH,
  NEARBY_SEARCH,
} from '../../constants/index'
import { selectSavedSearches } from '../../selectors'
import { TEXT } from './constants'
import { createSearchName } from './createSearchName'
import { type WeirdPartialListable, createSearchQuery } from './createSearchQuery'
import type { SaveSearchOrigin } from './getOrigin'

interface BaseCreateSearchArgs {
  email: string | undefined
  floorplan?: FloorplanListing<NormalFloorplanListingData> | null | undefined
  frequency?: undefined
  fromMessage?: boolean
  isLoggedIn: boolean
  noButter?: boolean
  /** Do not try to convert by throwing up sign up form */
  noConvert?: boolean
  origin: SaveSearchOrigin
  preventLoginPrompt?: boolean
  token?: string | undefined
  type?: typeof NEARBY_SEARCH | typeof HOOD_SEARCH
}

interface EntityCreateSearchArgs extends BaseCreateSearchArgs {
  entity: Listable | Property | null | undefined
  filters?: Partial<Filters>
}

interface FiltersCreateSearchArgs extends BaseCreateSearchArgs {
  entity?: WeirdPartialListable | Listable | Property | null | undefined
  filters: Partial<Filters>
}

export type CreateSearchArgs = EntityCreateSearchArgs | FiltersCreateSearchArgs

/**
 * Creates a search on the backend and an search for the user.
 *
 * @param searchConfig
 */
export const createSearch =
  (searchConfig: CreateSearchArgs) =>
  async (dispatch: Dispatch, getState: GetState, { optimizely }: ExtraThunkArg) => {
    const state = getState()
    const {
      email,
      entity,
      filters,
      floorplan,
      frequency,
      fromMessage,
      isLoggedIn,
      noButter,
      noConvert,
      origin,
      token,
      type = NEARBY_SEARCH,
    } = searchConfig
    const isMobile = selectIsMobile(state)
    const user = selectUser(state)
    const name = createSearchName({
      type,
      entity,
      filters,
    })

    // Optimizely visited experiments
    const visitedExperiments = getVisitedExperiments(optimizely)

    const query = createSearchQuery({
      type,
      entity,
      floorplan,
      origin,
      filters,
      isMobile,
      token,
      variationMap: visitedExperiments,
    })

    const searchButter = (action: ReturnType<typeof addErrorButterBar>) => {
      if (!noButter && !fromMessage) {
        dispatch(action)
      }
    }
    // assert user !== null

    // Check that the query has valid location information
    if (!(query.url || (query.minLng && query.maxLng && query.minLat && query.maxLat))) {
      searchButter(
        addErrorButterBar({
          isSearchButter: true,
          message: TEXT.NOT_VALID_QUERY,
        })
      )
      trackGAEvent('Alert', 'error', JSON.stringify(query))
      throw new Error(TEXT.NOT_VALID_QUERY)
    }

    const params: {
      email?: string
      email_frequency: typeof DEFAULT_ALERT_FREQUENCY
      name: typeof name
      query: ReturnType<typeof getAPIFriendlyFilters>
      user_id?: number
    } = {
      name,
      email_frequency: frequency !== undefined ? frequency : DEFAULT_ALERT_FREQUENCY,
      query: getAPIFriendlyFilters({ filters: query }),
    }
    if (isLoggedIn) {
      params.user_id = user.userId
    } else if (email) {
      params.email = email
    } else {
      searchButter(
        addErrorButterBar({
          isSearchButter: true,
          message: TEXT.NO_EMAIL,
        })
      )
      throw new Error(TEXT.NO_EMAIL)
    }
    dispatch(startCreatingSavedSearch())
    if (!noButter) {
      dispatch(setToastType(ALERT_CREATION_TOAST_TYPE.CREATING_SEARCH))
    }

    let response: Awaited<ReturnType<(typeof search)['save']>> | null = null
    try {
      try {
        response = await search.save(params)
        // update saved searches with the minimum information necessary to properly update the UI
        const { availableBefore, availableAfter, ...query } = params.query
        const target = { search_id: response.searchId, ...params, query }
        const savedSearch = new SavedSearch(target)
        const existingSavedSearches = selectSavedSearches(getState())
        const updated = [savedSearch, ...existingSavedSearches]
        // @ts-expect-error
        dispatch(updateSavedSearches({ savedSearches: updated }))
      } catch (err: unknown) {
        dispatch(errorCreatingSavedSearch())
        // early exit condition with no butter
        if (noButter) {
          throw err
        }
        // safe-guard check
        if (!err || typeof err !== 'object' || !('data' in err) || !err.data) {
          logger.error('error saving search', params, err)
          searchButter(
            addErrorButterBar({
              isSearchButter: true,
              message: TEXT.FAIL,
            })
          )
        }
        if (
          err &&
          typeof err === 'object' &&
          'data' in err &&
          err.data &&
          typeof err.data === 'object' &&
          'reason' in err.data &&
          err.data.reason
        ) {
          switch (err.data.reason) {
            case API_RESPONSE.MAXIMUM_SEARCHES: {
              logger.info('save search failed: max searches reached', params)
              if (!fromMessage) {
                if (!isLoggedIn && email) {
                  dispatch(setSavedSearchEmail(email))
                }
                dispatch(setToastType(ALERT_CREATION_TOAST_TYPE.MAX_SEARCH))
              }
              break
            }
            case API_RESPONSE.INVALID_EMAIL: {
              logger.info('save search failed: invalid email', params)
              // invalid email, reset user's email
              if (!isLoggedIn && user) {
                const localUserData = { ...user.toJSON(), email: '' }
                saveUserToLocalStorage(localUserData)
                dispatch(setLocalUser(new User(localUserData as RawLocalUser)))
              }
              searchButter(
                addErrorButterBar({
                  isSearchButter: true,
                  message: TEXT.NOT_VALID_EMAIL,
                })
              )
              break
            }
            case API_RESPONSE.OTHER_USER: {
              logger.info('save search failed: email of other user', params)
              searchButter(
                addErrorButterBar({
                  isSearchButter: true,
                  message: TEXT.EXISTING_USER,
                })
              )
              dispatch(pushSignInUrl({ email, referrer: REFERRERS.SEARCH_EXISTING_USER }))
              break
            }
            case API_RESPONSE.TOKEN_CONSUMED: {
              logger.info('save search failed: token consumed', params)
              searchButter(
                addErrorButterBar({
                  isSearchButter: true,
                  message: TEXT.CONSUMED,
                })
              )
              break
            }
            case API_RESPONSE.CSRF_NOT_SET: {
              if (isCookieError(err)) {
                searchButter(
                  addErrorButterBar({
                    isSearchButter: true,
                    message:
                      'We require cookies to save searches. Please enable your cookies and try again.',
                  })
                )
              } else {
                logger.info('save search failed: CSRF cookie not set', params)
                searchButter(
                  addErrorButterBar({
                    isSearchButter: true,
                    message: TEXT.REFRESH,
                  })
                )
              }
              break
            }
            default: {
              logger.error('save search failed', params, err)
              searchButter(
                addErrorButterBar({
                  isSearchButter: true,
                  message: TEXT.FAIL,
                })
              )
            }
          }
        }
        throw err
      }
      dispatch(Optimizely.track({ eventKey: ALERT_CREATED }))
      dispatch(doneCreatingSavedSearch())
      trackGAEvent('Alerts', 'create', origin)

      let productId
      if (entity) {
        const { buildingId, listingId, groupId } = entity
        productId = buildingId ? `b${buildingId}` : String(listingId)
        const savedSearchEntityIds = localStorageSingleton.get('savedSearchEntityIds') ?? []
        const savedSearchOn = localStorageSingleton.get('savedSearchOn') ?? {}
        if (groupId && !savedSearchEntityIds.includes(groupId)) {
          localStorageSingleton.set('savedSearchEntityIds', [...savedSearchEntityIds, groupId])
        }
        if (groupId && !savedSearchOn[groupId]) {
          const timestamp = ~~(Date.now() / 1000)
          localStorageSingleton.set('savedSearchOn', { ...savedSearchOn, [groupId]: timestamp })
        }
      }
      Marketing.trackAlert(query, {
        customerId: isLoggedIn ? user.userId : undefined,
        email,
        firstName: user.firstName,
        fromMessage,
        lastName: user.lastName,
        name, // search.name
        origin,
        productId,
        searchId: response?.searchId,
      })

      if (email) {
        identify(email)
      }
      const extraPayload: {
        [PROP_NAMES.PAGE_NAME]?: ReturnType<typeof getPageNameFromRouteProps>
      } = {}
      if (type === NEARBY_SEARCH) {
        const pageName = getPageNameFromRouteProps({
          routeProps: {
            location: { pathname: window?.location?.pathname, search: window.location.search },
          },
          isMobile,
        })
        extraPayload[PROP_NAMES.PAGE_NAME] = pageName
      }
      track({
        event: EVENT_NAMES.CREATE_ALERT,
        payload: {
          [PROP_NAMES.ORIGIN]: origin,
          [PROP_NAMES.FORMATTED_ORIGIN]: getFormattedOrigin(origin),
          [PROP_NAMES.EMAIL_NOTIFICATIONS]: !!frequency,
          ...extraPayload,
        },
      })

      if (isLoggedIn && !fromMessage) {
        dispatch(setToastType(ALERT_CREATION_TOAST_TYPE.SEARCH_SAVED))
      } else {
        // if user updates the email address, update the local user info
        if (user?.email && user.email !== email) {
          const existingLocalUser = localStorageSingleton.get('user') || {}
          const localUserData = {
            ...existingLocalUser,
            ...user.toJSON(),
            email,
          }
          saveUserToLocalStorage(localUserData)
          dispatch(setLocalUser(new User(localUserData)))
        }
        if (!noConvert) {
          dispatch(setToastType(ALERT_CREATION_TOAST_TYPE.SEARCH_SAVED))
          // Logged out user, let's try to convert
          dispatch(pushSignUpUrl({ email, referrer: REFERRERS.SEARCH }))
        }
      }
      return response
    } catch (err: unknown) {
      if (response === null) {
        if (typeof err !== 'object' || !err || !('data' in err) || !err.data) {
          if (!isCommonAuthError(err)) {
            logger.error('search was not saved', params, err)
          }
        }
        throw err
      }
      if (!isCommonAuthError(err)) {
        logger.error('error after search.save success', response, params, err)
      }
      return response
    }
  }
